import { Delete, Move, Reset } from "assets/icons/Common";
import classNames from "classnames";
import { GoButton, Row, Space } from "components/primitives";
import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";

import { MoveIconBtn } from "../FreeText/FreeText.styles";

const dividentLength = 40;
const CartesianPlane: React.FC = ({ state, pixelPosition, settings, setState }: any) => {
  const [coordinate, setCoordinate] = useState<any>({ xValue: 0, yValue: 0 });
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const [draggedEntireLineIndex, setDraggedEntireLineIndex] = useState<number | null>(null);

  const [draggedLineIndex, setDraggedLineIndex] = useState<{
    lineIndex: number;
    end: "start" | "end";
  } | null>(null);
  const [points, setPoints] = useState<
    {
      x: number;
      y: number;
      color?: string;
      label?: string;
      prevX?: number;
      prevY?: number;
    }[]
  >([]);
  const [lines, setLines] = useState<
    {
      start: {
        x: number;
        y: number;
        color?: string;
        label?: string;
      };
      end: { x: number; y: number };
    }[]
  >([]);

  const [leftRange, setLeftRange] = useState<number>(10);
  const [rightRange, setRightRange] = useState<number>(10);
  const [bottomRange, setTopRange] = useState<number>(10);
  const [topRange, setBottomRange] = useState<number>(10);
  const [xStep, setXStep] = useState(1);
  const [yStep, setYStep] = useState(1);
  const [gridSpacingX, setGridSpacingX] = useState(settings.grid.range.gridSpacingX);
  const [gridSpacingY, setGridSpacingY] = useState(settings.grid.range.gridSpacingY);
  const pointRadius = 8;
  const lineWidth = 4;

  // useEffect(() => {
  //   const pointIndex = settings?.grid?.pointIndex;

  //   if (pointIndex !== null) {
  //     const index = pointIndex;
  //     const range = settings.grid.range;
  //     const updatedPoints = [...state.grid.point];
  //     updatedPoints[index] = {
  //       ...updatedPoints[index],
  //       x: range.xValue,
  //       y: range.yValue,
  //     };
  //     setPoints(updatedPoints);
  //   }
  // }, [settings, state]);

  useEffect(() => {
    const rangeX = leftRange + rightRange + 2;
    const gridSizeX = pixelPosition.width / rangeX;

    const rangeY = topRange + bottomRange + 2;
    const gridSizeY = (pixelPosition.height - dividentLength) / rangeY;

    if (pixelPosition.width > 0) {
      setGridSpacingX(gridSizeX);
    }

    if (pixelPosition.height > 0) {
      setGridSpacingY(gridSizeY);
    }
  }, [pixelPosition, leftRange, rightRange, topRange, bottomRange, settings]);

  useEffect(() => {
    const settingRange = settings.grid.range;
    setLeftRange(Math.abs(settingRange.leftRange));
    setRightRange(Math.abs(settingRange.rightRange));
    setTopRange(Math.abs(settingRange.bottomRange));
    setBottomRange(Math.abs(settingRange.topRange));
    setXStep(settingRange.xStep);
    setYStep(settingRange.yStep);
  }, [settings.grid.range]);

  /**Code when grid changes the points should automatically adjust..need some adjustment */

  // useEffect(() => {
  //   const mapPoint = state.grid.point.map((point: any) => {
  //     const isGreaterGrid =
  //       point?.initialGrid && point?.initialGrid < settings.grid.gridSize;

  //     return {
  //       ...point,
  //       prevX: point.x ?? 0,
  //       prevY: point.y ?? 0,
  //       x: isGreaterGrid ? point.x * 2 : point.prevX ?? point.x,
  //       y: isGreaterGrid ? point.y * 2 : point.prevY ?? point.y,
  //     };
  //   });
  //   setPoints(mapPoint);
  // }, [state.grid.point, state.grid.line, settings.grid.gridSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawCoordinatePlane = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const { originX, originY } = getOrigin();
      const tickLength = 6;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = "10px Arial";
      ctx.fillStyle = "black";

      ctx.strokeStyle = "#e0e0e0";
      ctx.lineWidth = 0.5;

      for (
        let x = originX - leftRange * gridSpacingX * xStep;
        x <= originX + rightRange * gridSpacingX * xStep + 0.1; // Add small buffer
        x += gridSpacingX * xStep
      ) {
        const gridX = Math.round((x - originX) / (gridSpacingX * xStep));

        // Ensure gridX is within valid range
        if (gridX < -leftRange || gridX > rightRange) continue;

        if (x >= 0 && x <= canvas.width) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvas.height);
          ctx.stroke();

          // Draw grid numbers except for origin
          if (gridX !== 0) {
            ctx.beginPath();
            ctx.moveTo(x, originY - tickLength);
            ctx.lineTo(x, originY + tickLength);
            ctx.stroke();
            ctx.fillText((gridX * xStep).toString(), x - 3, originY + 12);
          }

          // // Explicitly draw the "10" if at the right edge
          // if (gridX === 10) {
          //   ctx.fillText("10", x - 3, originY + 12);
          // }
        }
      }

      for (
        let y = originY - topRange * gridSpacingY * yStep;
        y <= originY + bottomRange * gridSpacingY * yStep + 0.1; // Add small buffer
        y += gridSpacingY * yStep
      ) {
        const gridY = Math.round((originY - y) / (gridSpacingY * yStep));

        // Ensure gridY is within valid range
        if (gridY < -bottomRange || gridY > topRange) continue;

        if (y >= 0 && y <= canvas.height) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.width, y);
          ctx.stroke();

          // Draw grid numbers except for origin
          if (gridY !== 0) {
            ctx.beginPath();
            ctx.moveTo(originX - tickLength, y);
            ctx.lineTo(originX + tickLength, y);
            ctx.stroke();
            ctx.fillText((gridY * yStep).toString(), originX - 18, y + 3);
          }

          // // Explicitly draw the "-10" if at the bottom edge
          // if (gridY === -10) {
          //   ctx.fillText("-10", originX - 18, y + 3);
          // }
        }
      }

      ctx.strokeStyle = "black";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(originX, 0);
      ctx.lineTo(originX, canvas.height);
      ctx.moveTo(0, originY);
      ctx.lineTo(canvas.width, originY);
      ctx.stroke();

      ctx.fillStyle = "black";

      ctx.beginPath();
      ctx.moveTo(canvas.width - 10, originY - 5);
      ctx.lineTo(canvas.width, originY);
      ctx.lineTo(canvas.width - 10, originY + 5);
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(10, originY - 5);
      ctx.lineTo(0, originY);
      ctx.lineTo(10, originY + 5);
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(originX - 5, 10);
      ctx.lineTo(originX, 0);
      ctx.lineTo(originX + 5, 10);
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(originX - 5, canvas.height - 10);
      ctx.lineTo(originX, canvas.height);
      ctx.lineTo(originX + 5, canvas.height - 10);
      ctx.fill();

      ctx.fillText("x", canvas.width - 25, originY - 10);
      ctx.fillText("y", originX + 10, 25);

      points.forEach((point) => {
        const x = originX + point?.x * gridSpacingX;
        const y = originY - point?.y * gridSpacingY;
        ctx.beginPath();
        ctx.fillStyle = point?.color ?? "#8455a2";
        ctx.arc(x, y, pointRadius, 0, 2 * Math.PI);
        ctx.fill();

        if (point?.label) {
          ctx.fillStyle = "black";
          ctx.font = "12px Arial";
          ctx.fillText(point.label, x + 6, y - 6);
        }
      });

      lines.forEach((line) => {
        const startX = originX + line.start.x * gridSpacingX;
        const startY = originY - line.start.y * gridSpacingY;
        const endX = originX + line.end.x * gridSpacingX;
        const endY = originY - line.end.y * gridSpacingY;
        ctx.beginPath();
        ctx.strokeStyle = line.start?.color ?? "#8455a2";
        ctx.lineWidth = lineWidth;
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        if (line.start.label) {
          const midX = (startX + endX) / 2;
          const midY = (startY + endY) / 2;
          ctx.fillStyle = "black";
          ctx.font = "12px Arial";
          ctx.fillText(line.start.label, midX + 26, midY - 6);
        }
      });
    };

    drawCoordinatePlane();
  }, [
    points,
    lines,
    leftRange,
    rightRange,
    bottomRange,
    topRange,
    xStep,
    yStep,
    pixelPosition,
    gridSpacingX,
    gridSpacingY,
  ]);

  const onAdd = (type: string) => {
    let body: any = {};
    if (type === "point") {
      console.log("state.grid[type]", state.grid[type]);

      body = {
        ...state.grid,
        [type]: [...state.grid[type], { x: 0, y: 0 }],
      };
    } else {
      body = {
        ...state.grid,
        [type]: [
          ...state.grid[type],
          {
            start: { x: -1, y: -1 },
            end: { x: 1, y: 1 },
          },
        ],
      };
    }

    setState({ grid: body });
    setPoints(body.point);
    setLines(body.line);
  };

  const getOrigin = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { originX: 0, originY: 0 };
    const originX = canvas.width * (leftRange / (leftRange + rightRange));
    const originY = canvas.height * (topRange / (bottomRange + topRange));
    return { originX, originY };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const { originX, originY } = getOrigin();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const endpointRadius = 30; // Clickable area for endpoints
    const lineClickThreshold = 5; // Threshold for line clicks

    // Check if a point was clicked
    const clickedPointIndex = points.findIndex((point, index) => {
      const pointX = originX + point?.x * gridSpacingX;
      const pointY = originY - point?.y * gridSpacingY;

      const gridX = Math.round((mouseX - originX) / gridSpacingX);
      const gridY = Math.round((originY - mouseY) / gridSpacingY);

      setState({
        grid: {
          ...state.grid,
          pointIndex: index,
          type: "point",
        },
      });

      setCoordinate({ xValue: gridX, yValue: gridY });

      return (
        Math.abs(mouseX - pointX) < endpointRadius && Math.abs(mouseY - pointY) < endpointRadius
      );
    });

    if (clickedPointIndex !== -1) {
      setIsDragging(true);
      setDraggedPointIndex(clickedPointIndex); // Enable point dragging
      return;
    }

    // Check if an endpoint was clicked
    const clickedLineEndpoint = lines.findIndex((line, index) => {
      const startX = originX + line.start.x * gridSpacingX;
      const startY = originY - line.start.y * gridSpacingY;
      const endX = originX + line.end.x * gridSpacingX;
      const endY = originY - line.end.y * gridSpacingY;

      if (
        Math.abs(mouseX - startX) < endpointRadius &&
        Math.abs(mouseY - startY) < endpointRadius
      ) {
        setIsDragging(true);
        setDraggedLineIndex({ lineIndex: index, end: "start" });
        setState({
          grid: {
            ...state.grid,
            type: "line",
            lineIndex: index,
          },
        });
        return true;
      }

      if (Math.abs(mouseX - endX) < endpointRadius && Math.abs(mouseY - endY) < endpointRadius) {
        setIsDragging(true);
        setDraggedLineIndex({ lineIndex: index, end: "end" });
        setState({
          grid: {
            ...state.grid,
            type: "line",
            lineIndex: index,
          },
        });
        return true;
      }

      return false;
    });

    if (clickedLineEndpoint !== -1) return;

    // Check if a line body was clicked
    const clickedLineIndex = lines.findIndex((line, index) => {
      const startX = originX + line.start.x * gridSpacingX;
      const startY = originY - line.start.y * gridSpacingY;
      const endX = originX + line.end.x * gridSpacingX;
      const endY = originY - line.end.y * gridSpacingY;

      const lineLength = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));

      const distance =
        Math.abs(
          (endY - startY) * mouseX - (endX - startX) * mouseY + endX * startY - endY * startX,
        ) / lineLength;

      return distance <= lineClickThreshold;
    });

    if (clickedLineIndex !== -1) {
      const draggedLine = lines[clickedLineIndex];
      const startX = originX + draggedLine.start.x * gridSpacingX;
      const startY = originY - draggedLine.start.y * gridSpacingY;

      setIsDragging(true);

      setDragOffset({
        x: mouseX - startX,
        y: mouseY - startY,
      });
      setState({
        grid: {
          ...state.grid,
          type: "line",
          lineIndex: clickedLineIndex,
        },
      });
      setDraggedEntireLineIndex(clickedLineIndex);
      return;
    }

    // Reset dragging states if no valid interaction
    setIsDragging(false);
    setDraggedPointIndex(null);
    setDraggedLineIndex(null);
    setDragOffset(null);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const { originX, originY } = getOrigin();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const gridX = Math.round((mouseX - originX) / gridSpacingX);
    const gridY = Math.round((originY - mouseY) / gridSpacingY);

    if (gridX < -leftRange || gridX > rightRange || gridY > topRange || gridY < -bottomRange) {
      return; // Exit if outside grid boundaries
    }

    if (draggedPointIndex !== null) {
      // Dragging a point
      const updatedPoints = [...points];
      const draggedPoint = points[draggedPointIndex];
      updatedPoints[draggedPointIndex] = {
        ...updatedPoints[draggedPointIndex],
        x: gridX,
        y: gridY,
      };

      // Update lines connected to this point
      const updatedLines = lines.map((line) => {
        const isStartConnected = line.start.x === draggedPoint.x && line.start.y === draggedPoint.y;
        const isEndConnected = line.end.x === draggedPoint.x && line.end.y === draggedPoint.y;

        if (isStartConnected) {
          return {
            ...line,
            start: { ...line.start, x: gridX, y: gridY },
          };
        }
        if (isEndConnected) {
          return {
            ...line,
            end: { x: gridX, y: gridY },
          };
        }
        return line;
      });

      setPoints(updatedPoints);
      setLines(updatedLines);

      // Update state with new grid values
      setState({
        grid: {
          ...state.grid,
          point: updatedPoints,
          line: updatedLines,
        },
      });
      setCoordinate({ xValue: gridX, yValue: gridY });
    } else if (draggedLineIndex) {
      // Dragging a line endpoint
      const updatedLines = [...lines];
      const { lineIndex, end } = draggedLineIndex;
      updatedLines[lineIndex][end] = {
        ...updatedLines[lineIndex].start,
        x: gridX,
        y: gridY,
      };

      setLines(updatedLines);
      setState({
        grid: { ...state.grid, line: updatedLines },
      });
    } else if (draggedEntireLineIndex !== null) {
      // Dragging the entire line
      if (!dragOffset) return;
      const gridX = Math.round((mouseX - dragOffset.x - originX) / gridSpacingX);
      const gridY = Math.round((originY - (mouseY - dragOffset.y)) / gridSpacingY);
      const updatedLines = [...lines];
      const draggedLine = updatedLines[draggedEntireLineIndex];
      const deltaX = gridX - draggedLine.start.x;
      const deltaY = gridY - draggedLine.start.y;

      updatedLines[draggedEntireLineIndex] = {
        start: {
          ...updatedLines[draggedEntireLineIndex].start,
          x: draggedLine.start.x + deltaX,
          y: draggedLine.start.y + deltaY,
        },
        end: {
          x: draggedLine.end.x + deltaX,
          y: draggedLine.end.y + deltaY,
        },
      };

      setLines(updatedLines);
      setState({
        grid: { ...state.grid, line: updatedLines },
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedPointIndex(null);
    setDraggedLineIndex(null);
    setDraggedEntireLineIndex(null);
  };

  const reset = () => {
    setState({
      grid: {
        point: [],
        line: [],
        range: {
          leftRange: -10,
          rightRange: 10,
          bottomRange: -10,
          topRange: 10,
          xStep: 1,
          yStep: 1,
          xValue: 0,
          yValue: 0,
          gridSpacingX: 25,
          gridSpacingY: 25,
        },
        disableDrag: true,
        pointIndex: null,
        lineIndex: null,
        type: null,
        gridSize: 10,
      },
    });
    setPoints([]);
    setLines([]);
    setCoordinate({ xValue: 0, yValue: 0 });
  };

  const onChangeCoordinate = (key: string, value: string | number) => {
    const index = state.grid.pointIndex;
    const updatedPoints = [...points];
    if (index !== null) {
      updatedPoints[index] = {
        ...updatedPoints[index],
        [key]: value,
      };

      setPoints(updatedPoints);
      setState({ grid: { ...state.grid, point: updatedPoints } });
    }
  };

  const remove = () => {
    if (state.grid.type === "point" && state.grid.pointIndex !== null) {
      state.grid.point.splice(state.grid.pointIndex, 1);
    }

    if (state.grid.type === "line" && state.grid.lineIndex !== null) {
      state.grid.line.splice(state.grid.lineIndex, 1);
    }

    setState({ grid: state.grid });
  };

  return (
    <>
      <ToolBar>
        <OptionButton onClick={() => onAdd("point")}>
          <Dot /> Add Point
        </OptionButton>
        <OptionButton onClick={() => onAdd("line")}>
          <Line /> Add Line
        </OptionButton>
        <div style={{ marginLeft: 10 }} />
        <Row gap={10}>
          x
          <Input
            type="number"
            value={coordinate.xValue}
            min={settings.grid.range.leftRange}
            max={settings.grid.range.rightRange}
            onChange={(e) => {
              if (e.target.value === "") {
                setCoordinate({ ...coordinate, xValue: "" });
                onChangeCoordinate("x", "");
                return;
              }

              const value = +e.target.value;
              if (
                value <= Math.abs(settings.grid.range.leftRange) ||
                value <= Math.abs(settings.grid.range.rightRange)
              ) {
                setCoordinate({ ...coordinate, xValue: value });
                onChangeCoordinate("x", value);
              }
            }}
          />
        </Row>
        <Space />
        <Row gap={10}>
          y
          <Input
            type="number"
            value={coordinate.yValue}
            min={settings.grid.range.bottomRange}
            max={settings.grid.range.topRange}
            onChange={(e) => {
              if (e.target.value === "") {
                setCoordinate({ ...coordinate, yValue: "" });
                onChangeCoordinate("y", "");
                return;
              }

              const value = +e.target.value;
              if (
                value <= Math.abs(settings.grid.range.topRange) ||
                value <= Math.abs(settings.grid.range.bottomRange)
              ) {
                setCoordinate({ ...coordinate, yValue: value });
                onChangeCoordinate("y", value);
              }
            }}
          />
        </Row>
        <Row gap={8}>
          <GoButton withShadow={false} Icon={Delete} onClick={remove} height="3cqw" width="3cqw" />
          <GoButton withShadow={false} Icon={Reset} onClick={reset} height="3cqw" width="3cqw" />
        </Row>
      </ToolBar>
      <CanvasContainer>
        <canvas
          ref={canvasRef}
          width={pixelPosition.width}
          height={pixelPosition.height - dividentLength}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
      </CanvasContainer>
      <MoveIconBtn Icon={Move} />
    </>
  );
};

export default CartesianPlane;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 4px 4px 4px 24px;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const CanvasContainer = styled.div.attrs({
  className: classNames("no-drag"),
})`
  height: 100%;
  padding: 0;
`;

const OptionButton = styled.button<{ selected?: boolean; center?: boolean }>`
  height: 3cqw;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.center ? "center" : "flex-start")};
  gap: 8px;
  font-size: 1.5cqw;
  cursor: pointer;
  border: 2px solid ${(props) => (props.selected ? "#007BFF" : "#ccc")};
  background-color: ${(props) => (props.selected ? "#e7f1ff" : "#f0f0f0")};
  color: ${(props) => (props.selected ? "#007BFF" : "#333")};
  border-radius: 5px;

  &:hover {
    border-color: #007bff;
    background-color: #e7f1ff;
  }
`;

const Dot = styled.div`
  width: 2cqw;
  height: 2cqw;
  border: 1px solid #d0d0d0;
  border-radius: 3cqw;
  background-color: #d0d0d0;
`;

const Line = styled.div`
  width: 4cqw;
  height: 1cqh;
  background-color: #e0e0e0;
`;

const Input = styled.input`
  font-size: 2cqw;
  border: 1px solid #ccc;
  border-radius: 4px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;
